// Deterministic allocation dataset: 10 products, 4 warehouses, 20 users, 200 customers.

function mkRng(seed: number) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    const r =
      (Math.imul(t ^ (t >>> 15), 1 | t) +
        Math.imul(
          Math.imul(t ^ (t >>> 15), 1 | t) ^
            (Math.imul(t ^ (t >>> 15), 1 | t) >>> 7),
          61 | Math.imul(t ^ (t >>> 15), 1 | t),
        )) ^
      (Math.imul(t ^ (t >>> 15), 1 | t) +
        Math.imul(
          Math.imul(t ^ (t >>> 15), 1 | t) ^
            (Math.imul(t ^ (t >>> 15), 1 | t) >>> 7),
          61 | Math.imul(t ^ (t >>> 15), 1 | t),
        ));
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// Simpler seeded RNG (mulberry32)
function seedRng(seed: number) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
function rngPick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}
function rngInt(rng: () => number, lo: number, hi: number): number {
  return Math.floor(rng() * (hi - lo + 1)) + lo;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  uom: string;
}

export const PRODUCTS: Product[] = [
  {
    id: 'P-1',
    sku: 'WIR-12THHN-500',
    name: "12 AWG THHN Copper, 500'",
    uom: 'roll',
  },
  {
    id: 'P-2',
    sku: 'NMB-14-2-250',
    name: "14/2 NM-B Romex, 250'",
    uom: 'roll',
  },
  { id: 'P-3', sku: 'EMT-075-10', name: 'EMT Conduit 3/4" × 10\'', uom: 'ea' },
  { id: 'P-4', sku: 'PVC-050-10', name: 'PVC Conduit 1/2" × 10\'', uom: 'ea' },
  {
    id: 'P-5',
    sku: 'REC-20DECORA-10',
    name: '20A Decora Receptacle, 10-pk',
    uom: 'pk',
  },
  { id: 'P-6', sku: 'LED-2X4-277', name: 'LED Troffer 2×4, 277V', uom: 'ea' },
  {
    id: 'P-7',
    sku: 'BRK-QO-20A',
    name: 'QO 20A Breaker, single-pole',
    uom: 'ea',
  },
  { id: 'P-8', sku: 'PNL-200A-LC', name: '200A Loadcenter, NEMA 1', uom: 'ea' },
  {
    id: 'P-9',
    sku: 'CON-LT-075',
    name: 'Liquidtight Connector 3/4"',
    uom: 'ea',
  },
  {
    id: 'P-10',
    sku: 'PVI-76-1P',
    name: 'PV Inverter 7.6kW, 1-phase',
    uom: 'ea',
  },
];

export interface Warehouse {
  id: string;
  name: string;
  loc: string;
  short: string;
}

export const WAREHOUSES: Warehouse[] = [
  { id: 'WH-1', name: 'Newark DC', loc: 'Newark, NJ', short: 'NWK' },
  { id: 'WH-2', name: 'Atlanta DC', loc: 'Atlanta, GA', short: 'ATL' },
  { id: 'WH-3', name: 'Dallas DC', loc: 'Dallas, TX', short: 'DAL' },
  { id: 'WH-4', name: 'Reno DC', loc: 'Reno, NV', short: 'RNO' },
];

const FIRST = [
  'Angel',
  'Maya',
  'Daniel',
  'Sara',
  'Priya',
  'Marcus',
  'Lena',
  'Rafael',
  'Naomi',
  'Owen',
  'Cassie',
  'Jonas',
  'Imani',
  'Theo',
  'Hana',
  'Devon',
  'Anika',
  'Ezra',
  'Vera',
  'Marco',
];
const LAST = [
  'Garza',
  'Sun',
  'Hwang',
  'Ortiz',
  'Patel',
  'Bauer',
  'Khan',
  'Romero',
  'Aoki',
  'Walsh',
  'Reid',
  'Holm',
  'Okafor',
  'Cruz',
  'Park',
  'Carter',
  'Joshi',
  'Levin',
  'Tran',
  'Russo',
];

export interface User {
  id: string;
  name: string;
  fullName: string;
  initials: string;
}

export const USERS: User[] = FIRST.map((f, i) => ({
  id: `U-${i + 1}`,
  name: `${f} ${LAST[i][0]}.`,
  fullName: `${f} ${LAST[i]}`,
  initials: `${f[0]}${LAST[i][0]}`,
}));

export const CURRENT_USER = USERS[0];

const CUSTOMER_NAMES = [
  'Ridgemont Electrical',
  'Pinehurst Supply',
  'Graybar Electric',
  'WESCO Distribution',
  'Mayer Electric',
  'Border States',
  'City Electric Supply',
  'Crescent Electric',
  'Elliott Electric',
  'Summit Electric',
  'Springfield Electric',
  'Madison Electric',
  'Independent Electric',
  'United Electric',
  'Capital Electric',
  'Stuart C. Irby',
  'Hagemeyer NA',
  'Sonepar US',
  'Codale Electric',
  'Mars Electric',
  'Hite Electric',
  'Schaedler Yesco',
  'Walters-Wholesale',
  'House of Lights',
  'Steiner Electric',
  'Echo Electric',
  'Cooper Electric',
  'Werner Electric',
  'Englewood Electric',
  'French-Gerleman',
  'Voss Electric',
  'Granite City',
  'Loeb Electric',
  'Womack Electric',
  'Wholesale Electric',
  'Brown Electrical',
  'Vance Trading',
  'Ace Electric',
  'BlueRidge Wholesale',
  'Cardinal Supply',
  'Harbor Electric',
  'Northland Industrial',
  'Pacific Supply',
  'Sierra Wholesale',
  'Coastal Electric',
  'Lake Erie Electric',
  'Gulf Coast Supply',
  'Northwest Electric',
  'Southland Electric',
  'Mountain Supply',
];
const SUFFIXES = ['Co.', 'LLC', 'Group', 'Distribution', 'Holdings'];
const REGION_LIST = ['NE', 'SE', 'MW', 'SW', 'W'] as const;

export const REGION_LABEL: Record<string, string> = {
  NE: 'Northeast',
  SE: 'Southeast',
  MW: 'Midwest',
  SW: 'South',
  W: 'West',
};

const CITIES_BY_REGION: Record<string, [string, string][]> = {
  NE: [
    ['Newark', 'NJ'],
    ['Boston', 'MA'],
    ['Hartford', 'CT'],
    ['Buffalo', 'NY'],
    ['Pittsburgh', 'PA'],
    ['Albany', 'NY'],
    ['Providence', 'RI'],
    ['Philadelphia', 'PA'],
  ],
  SE: [
    ['Atlanta', 'GA'],
    ['Charlotte', 'NC'],
    ['Tampa', 'FL'],
    ['Jacksonville', 'FL'],
    ['Nashville', 'TN'],
    ['Raleigh', 'NC'],
    ['Birmingham', 'AL'],
    ['Greenville', 'SC'],
  ],
  MW: [
    ['Chicago', 'IL'],
    ['Detroit', 'MI'],
    ['Indianapolis', 'IN'],
    ['Columbus', 'OH'],
    ['Cleveland', 'OH'],
    ['Milwaukee', 'WI'],
    ['Minneapolis', 'MN'],
    ['St. Louis', 'MO'],
  ],
  SW: [
    ['Dallas', 'TX'],
    ['Houston', 'TX'],
    ['Austin', 'TX'],
    ['San Antonio', 'TX'],
    ['Oklahoma City', 'OK'],
    ['Tulsa', 'OK'],
    ['Little Rock', 'AR'],
    ['Albuquerque', 'NM'],
  ],
  W: [
    ['Reno', 'NV'],
    ['Phoenix', 'AZ'],
    ['Las Vegas', 'NV'],
    ['Denver', 'CO'],
    ['Salt Lake City', 'UT'],
    ['Boise', 'ID'],
    ['Sacramento', 'CA'],
    ['Portland', 'OR'],
  ],
};

export interface Customer {
  id: string;
  name: string;
  loc: string;
  city: string;
  state: string;
  region: string;
  regionLabel: string;
  salesperson: string;
  tier: string;
}

export const CUSTOMERS: Customer[] = (() => {
  const rng = seedRng(20260513);
  const out: Customer[] = [];
  for (let i = 1; i <= 200; i++) {
    const base = CUSTOMER_NAMES[(i - 1) % CUSTOMER_NAMES.length];
    const dupePass = Math.floor((i - 1) / CUSTOMER_NAMES.length);
    const name =
      dupePass === 0
        ? base
        : `${base} ${rngPick(rng, SUFFIXES)} ${dupePass + 1}`;
    const region = rngPick(rng, [...REGION_LIST]);
    const [city, st] = rngPick(rng, CITIES_BY_REGION[region]);
    const salesperson = USERS[rngInt(rng, 0, USERS.length - 1)].id;
    out.push({
      id: `CX-${i}`,
      name,
      loc: `${city}, ${st}`,
      city,
      state: st,
      region,
      regionLabel: REGION_LABEL[region],
      salesperson,
      tier: rngPick(rng, ['Strategic', 'Major', 'Mid', 'SMB']),
    });
  }
  return out;
})();

export interface Snapshot {
  id: string;
  kind: 'weekly' | 'daily';
  label: string;
  date: string;
  shortLabel: string;
  isLatest?: boolean;
}

export const SNAPSHOTS: Snapshot[] = [
  {
    id: 'S-W18',
    kind: 'weekly',
    label: 'Weekly anchor · Apr 27',
    date: '2026-04-27',
    shortLabel: 'Apr 27 W',
  },
  {
    id: 'S-MAY4',
    kind: 'weekly',
    label: 'Weekly anchor · May 4',
    date: '2026-05-04',
    shortLabel: 'May 4 W',
  },
  {
    id: 'S-MAY8',
    kind: 'daily',
    label: 'Daily · Fri, May 8',
    date: '2026-05-08',
    shortLabel: 'May 8',
  },
  {
    id: 'S-MAY11',
    kind: 'weekly',
    label: 'Weekly anchor · May 11',
    date: '2026-05-11',
    shortLabel: 'May 11 W',
  },
  {
    id: 'S-MAY12',
    kind: 'daily',
    label: 'Daily · Tue, May 12',
    date: '2026-05-12',
    shortLabel: 'May 12',
  },
  {
    id: 'S-MAY13',
    kind: 'daily',
    label: 'Daily · Wed, May 13',
    date: '2026-05-13',
    shortLabel: 'Today',
    isLatest: true,
  },
];

export interface Provenance {
  wh: string;
  qty: number;
}

export interface Reason {
  tag: string;
  text: string;
}

export interface HorizonWeek {
  offset: number;
  isPast: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  label: string;
  year: number;
  alloc: number;
  consumed: number | null;
  over: boolean;
  projected: boolean;
}

export interface AllocationRow {
  rid: string;
  customer: string;
  product: string;
  qty: Record<string, number>;
  prov: Provenance[];
  confirmed: number;
  consumed: number;
  remainder: number;
  reason: Reason;
  updated: string;
  confidence: number;
  flagged: boolean;
  weekly: HorizonWeek[];
}

const REASONS = [
  {
    tag: 'shortage',
    text: 'WH-2 inbound delayed 2 days — reduced regional allocation pro-rata.',
  },
  {
    tag: 'rebalance',
    text: 'Rebalanced from {fromWH} to {toWH} based on Tuesday consumption trend.',
  },
  {
    tag: 'tier',
    text: 'Strategic-tier customer prioritized after pool shortage detected.',
  },
  {
    tag: 'order',
    text: 'Confirmed PO matched against forecast — bumped allocation to cover gap.',
  },
  {
    tag: 'rounding',
    text: 'Weekly anchor rolled forward; quantity rounded to case multiple.',
  },
  {
    tag: 'forecast',
    text: 'Forecast model lowered demand signal for this customer by 12%.',
  },
  {
    tag: 'manual',
    text: 'Sales override applied by {sp} on {date} — agent honored override.',
  },
  { tag: 'no-change', text: 'No material change vs prior snapshot.' },
];

export interface HorizonMeta {
  offset: number;
  isPast: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  label: string;
  year: number;
}

export const HORIZON: HorizonMeta[] = (() => {
  const arr: HorizonMeta[] = [];
  for (let i = -4; i <= 47; i++) {
    const w = ((19 + i - 1) % 52) + 1;
    const year = i + 19 > 52 ? 2027 : 2026;
    arr.push({
      offset: i,
      isPast: i < 0,
      isCurrent: i === 0,
      isFuture: i > 0,
      label: `W${w}`,
      year,
    });
  }
  return arr;
})();

export const ALLOCATIONS: AllocationRow[] = (() => {
  const rng = seedRng(0xabcd42);
  const out: AllocationRow[] = [];
  let rid = 1;
  for (const c of CUSTOMERS) {
    const nProducts = rngInt(rng, 1, 4);
    const productPool = [...PRODUCTS]
      .sort(() => rng() - 0.5)
      .slice(0, nProducts);
    for (const p of productPool) {
      const baseFloor: Record<string, number> = {
        'P-1': 20,
        'P-2': 12,
        'P-3': 80,
        'P-4': 120,
        'P-5': 18,
        'P-6': 8,
        'P-7': 50,
        'P-8': 4,
        'P-9': 60,
        'P-10': 2,
      };
      const base = baseFloor[p.id] + rngInt(rng, 0, baseFloor[p.id] * 3);

      const qty: Record<string, number> = {};
      const drift = () => Math.round(base * (0.85 + rng() * 0.3));
      const round5 = (n: number) => Math.round(n / 5) * 5;
      qty['S-W18'] = round5(drift());
      qty['S-MAY4'] = round5(drift());
      qty['S-MAY8'] = drift();
      qty['S-MAY11'] = round5(drift());
      qty['S-MAY12'] = drift();
      qty['S-MAY13'] = drift();

      if (rng() < 0.3) qty['S-MAY13'] = qty['S-MAY12'];

      const whCount = rng() < 0.55 ? 1 : 2;
      const whPool = [...WAREHOUSES].sort(() => rng() - 0.5).slice(0, whCount);
      let prov: Provenance[];
      if (whCount === 1) {
        prov = [{ wh: whPool[0].id, qty: qty['S-MAY13'] }];
      } else {
        const splitA = Math.round(qty['S-MAY13'] * (0.3 + rng() * 0.4));
        prov = [
          { wh: whPool[0].id, qty: splitA },
          { wh: whPool[1].id, qty: qty['S-MAY13'] - splitA },
        ];
      }

      const consumeRatio = rng();
      let consumed: number;
      if (consumeRatio < 0.08) {
        consumed =
          qty['S-MAY13'] +
          rngInt(rng, 1, Math.max(2, Math.round(qty['S-MAY13'] * 0.15)));
      } else if (consumeRatio < 0.5) {
        consumed = Math.round(qty['S-MAY13'] * (0.4 + rng() * 0.55));
      } else if (consumeRatio < 0.85) {
        consumed = Math.round(qty['S-MAY13'] * (0.1 + rng() * 0.35));
      } else {
        consumed = 0;
      }

      const confirmed = Math.max(
        consumed,
        Math.round(qty['S-MAY13'] * (0.5 + rng() * 0.5)),
      );

      const changed = qty['S-MAY13'] !== qty['S-MAY12'];
      const reasonTpl = changed
        ? rngPick(
            rng,
            REASONS.filter((r) => r.tag !== 'no-change'),
          )
        : REASONS[REASONS.length - 1];
      const fromWH = WAREHOUSES[rngInt(rng, 0, 3)].id;
      let toWH = WAREHOUSES[rngInt(rng, 0, 3)].id;
      while (toWH === fromWH) toWH = WAREHOUSES[rngInt(rng, 0, 3)].id;
      const reason: Reason = {
        tag: reasonTpl.tag,
        text: reasonTpl.text
          .replace('{fromWH}', fromWH)
          .replace('{toWH}', toWH)
          .replace('{sp}', USERS[rngInt(rng, 0, USERS.length - 1)].name)
          .replace('{date}', 'May 12'),
      };

      const tsHours = rngInt(rng, 0, 22);
      const tsMins = rngInt(rng, 0, 59);
      const updated = changed ? `${tsHours}h${tsMins}m ago` : '—';
      const confidence = changed ? 0.55 + rng() * 0.4 : 0.85 + rng() * 0.12;

      out.push({
        rid: `R-${rid++}`,
        customer: c.id,
        product: p.id,
        qty,
        prov,
        confirmed,
        consumed,
        remainder: qty['S-MAY13'] - consumed,
        reason,
        updated,
        confidence,
        flagged: false,
        weekly: [],
      });
    }
  }

  // Build 52-week horizon for each row
  const _hrng = seedRng(0xf09e2);
  for (const row of out) {
    const base = row.qty['S-MAY13'];
    const seasonalAmp = 0.1 + _hrng() * 0.15;
    const seasonalPhase = _hrng() * Math.PI * 2;
    const overProneness = _hrng() < 0.18 ? 1 : 0;
    row.weekly = HORIZON.map((wk) => {
      const seasonal =
        Math.sin((wk.offset / 26) * Math.PI + seasonalPhase) * seasonalAmp;
      let alloc = Math.round(base * (1 + seasonal + (_hrng() - 0.5) * 0.08));
      if (alloc < 1) alloc = 1;
      if (wk.isCurrent) alloc = row.qty['S-MAY13'];

      let consumed: number | null = null;
      if (wk.isPast) {
        const r = _hrng();
        consumed =
          r < 0.12
            ? Math.round(alloc * (1.05 + _hrng() * 0.15))
            : Math.round(alloc * (0.55 + _hrng() * 0.45));
      } else if (wk.isCurrent) {
        consumed = row.consumed;
      }

      let projOver = false;
      if (wk.isFuture) {
        const p = overProneness ? 0.22 : 0.04;
        const seasonalBoost = wk.offset >= 26 && wk.offset <= 40 ? 0.15 : 0;
        projOver = _hrng() < p + seasonalBoost;
      }

      return {
        ...wk,
        alloc,
        consumed,
        over: (consumed != null && consumed > alloc) || projOver,
        projected: wk.isFuture,
      };
    });
  }

  return out;
})();

export function findCustomer(id: string): Customer {
  return CUSTOMERS.find((c) => c.id === id)!;
}
export function findProduct(id: string): Product {
  return PRODUCTS.find((p) => p.id === id)!;
}
export function findWh(id: string): Warehouse {
  return WAREHOUSES.find((w) => w.id === id)!;
}
export function findUser(id: string): User {
  return USERS.find((u) => u.id === id)!;
}

export function whColor(id: string): string {
  const map: Record<string, string> = {
    'WH-1': '#5b6c8a',
    'WH-2': '#7e6a4f',
    'WH-3': '#8a5a5a',
    'WH-4': '#5a7c6e',
  };
  return map[id] || '#78716c';
}

export interface TraceStep {
  kind: 'input' | 'signal' | 'compute' | 'provenance' | 'output';
  text: string;
  confidence?: number;
}

export function buildTrace(row: AllocationRow): TraceStep[] {
  const cur = row.qty['S-MAY13'];
  const prev = row.qty['S-MAY12'];
  const delta = cur - prev;
  const cust = findCustomer(row.customer);
  const prod = findProduct(row.product);
  const steps: TraceStep[] = [];

  steps.push({
    kind: 'input',
    text: `Input: prior allocation ${prev} ${prod.uom} for ${cust.name} (${cust.regionLabel}).`,
  });

  if (row.reason.tag === 'shortage') {
    steps.push({
      kind: 'signal',
      text: `Detected supply signal: inbound ASN for ${row.prov[0].wh} pushed +2 days — regional pool short.`,
    });
    steps.push({
      kind: 'compute',
      text: `Allocated pool ${cust.regionLabel}: 100% → 88% pro-rata across customers.`,
    });
  } else if (row.reason.tag === 'rebalance') {
    const from = row.prov[0]?.wh || 'WH-1';
    const to = row.prov[1]?.wh || 'WH-2';
    steps.push({
      kind: 'signal',
      text: `Tuesday consumption for ${cust.regionLabel} was 18% above forecast at ${from}; ${to} ran cool.`,
    });
    steps.push({
      kind: 'compute',
      text: `Rebalanced source: shifted ${Math.abs(delta) || 4} ${prod.uom} from ${from} → ${to}.`,
    });
  } else if (row.reason.tag === 'tier') {
    steps.push({
      kind: 'signal',
      text: `${cust.tier}-tier customer (${cust.name}); shortage policy gives priority claim.`,
    });
    steps.push({
      kind: 'compute',
      text: `Held ${cust.name} flat at ${cur}; pro-rata absorbed by Mid/SMB tier instead.`,
    });
  } else if (row.reason.tag === 'order') {
    steps.push({
      kind: 'signal',
      text: `Inbound PO matched 4 lines for ${cust.name} on May 12 — committed forecast gap.`,
    });
    steps.push({
      kind: 'compute',
      text: `Bumped allocation +${Math.max(delta, 1)} ${prod.uom} to cover confirmed demand.`,
    });
  } else if (row.reason.tag === 'rounding') {
    steps.push({
      kind: 'signal',
      text: `Weekly anchor (May 11) carried qty ${row.qty['S-MAY11']}; daily snapshot drift within tolerance.`,
    });
    steps.push({
      kind: 'compute',
      text: `Rounded to nearest case multiple — ${cur} ${prod.uom}.`,
    });
  } else if (row.reason.tag === 'forecast') {
    steps.push({
      kind: 'signal',
      text: `Forecast model v2.3 lowered ${prod.name} demand for ${cust.regionLabel} by 12% this week.`,
    });
    steps.push({
      kind: 'compute',
      text: `Adjusted allocation ${delta >= 0 ? '+' : ''}${delta} ${prod.uom} vs prior daily.`,
    });
  } else if (row.reason.tag === 'manual') {
    steps.push({
      kind: 'signal',
      text: `Sales override applied yesterday — note attached by salesperson.`,
    });
    steps.push({
      kind: 'compute',
      text: `Honored override; ${cur} ${prod.uom} locked through next weekly anchor.`,
    });
  } else {
    steps.push({
      kind: 'signal',
      text: `All signals (consumption, forecast, ASN) within tolerance.`,
    });
    steps.push({
      kind: 'compute',
      text: `Carried prior allocation forward unchanged.`,
    });
  }

  const provText = row.prov.map((p) => `${p.wh} ${p.qty}`).join(' + ');
  steps.push({
    kind: 'provenance',
    text: `Sourced ${cur} ${prod.uom} as ${provText}.`,
  });

  steps.push({
    kind: 'output',
    text: `Output: ${cur} ${prod.uom} ${delta === 0 ? '(no change)' : delta > 0 ? `(+${delta})` : `(${delta})`}.`,
    confidence: row.confidence,
  });

  return steps;
}

export function buildDigest(
  filtered: AllocationRow[],
  compare: { from: string; to: string },
  stats: AllocStats,
): string {
  const fromSnap = SNAPSHOTS.find((s) => s.id === compare.from)!;
  const toSnap = SNAPSHOTS.find((s) => s.id === compare.to)!;
  if (filtered.length === 0) return 'No rows match the current scope.';

  const tally: Record<string, number> = {};
  for (const r of filtered) {
    if (r.qty[compare.to] === r.qty[compare.from]) continue;
    tally[r.reason.tag] = (tally[r.reason.tag] || 0) + 1;
  }
  const topTags = Object.entries(tally)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2);
  const tagLabel: Record<string, string> = {
    shortage: 'regional shortage rebalancing',
    rebalance: 'cross-warehouse rebalancing',
    tier: 'tier-priority holds',
    order: 'PO-driven bumps',
    rounding: 'anchor rounding',
    forecast: 'forecast adjustments',
    manual: 'sales overrides',
  };
  const tagPhrase =
    topTags.length === 0
      ? 'no material adjustments'
      : topTags.map(([t, n]) => `${n} ${tagLabel[t] || t}`).join(' and ');

  const pieces: string[] = [];
  if (stats.changedRows === 0) {
    pieces.push(
      `Allocations held steady from ${fromSnap.shortLabel.replace(' W', '')} to ${toSnap.shortLabel.replace(' W', '')}.`,
    );
  } else {
    pieces.push(
      `Agent revised ${stats.changedRows} ${stats.changedRows === 1 ? 'row' : 'rows'} since ${fromSnap.shortLabel.replace(' W', '')} (${stats.upRows} up, ${stats.downRows} down) — driven primarily by ${tagPhrase}.`,
    );
  }
  if (stats.overConsumed > 0) {
    pieces.push(
      `${stats.overConsumed} ${stats.overConsumed === 1 ? 'customer is' : 'customers are'} over-consumed against the current snapshot — review needed before next replenishment.`,
    );
  }
  if (toSnap.kind === 'weekly') {
    pieces.push(
      'Weekly anchor in effect; values are treated as authoritative until next refresh.',
    );
  }
  return pieces.join(' ');
}

export interface AllocStats {
  totalAlloc: number;
  totalRows: number;
  totalCustomers: number;
  changedRows: number;
  upRows: number;
  downRows: number;
  overConsumed: number;
  whCount: number;
  whLabels: string;
}
